import { useTranslation } from 'react-i18next';
import CatalogListHeader from '../../../components/CatalogListHeader/CatalogListHeader';
import { ActionIcon, Checkbox, Grid, Paper, Text, ThemeIcon, Tooltip } from '@mantine/core';
import { useLocation } from 'react-router-dom';
import CatalogContainer from '../../../components/CatalogContainer/CatalogContainer';
import moment from 'moment';
import { DatePickerInput, DatesRangeValue } from '@mantine/dates';
import { useMemo, useState } from 'react';
import { useGetScheduledDeliveries } from '../../../hooks/reports/queries';
import TableView from './TableView';
import { useSelector } from 'react-redux';
import useDownloadExcel from '../../../hooks/useDownloadExcel/useDownloadExcel';
import { IconFileSpreadsheet, IconInfoCircle } from '@tabler/icons-react';
import { exportScheduledDeliveriesReportCSV } from '../../../services/reports.service';

export interface ScheduledDeliveriesOrderData {
  orderId: any;
  storeId: any;
  folio: string;
  customerName: string;
  customerPhone: string;
  deliveryDate: Date;
  fromTime: string;
  toTime: string;
  servicesText: string;
  total: number;
  paidAmount: number;
  balance: number;
  status: string;
  createdAt: Date;
  storeName: string;
}

const ScheduledDeliveriesReport = () => {
  const { t } = useTranslation(['ReportsPage', 'FieldName', 'CommonButtons', 'Notifications']);
  const location = useLocation();
  const {
    company: { _id: companyId }
  } = useSelector((state: any) => state.user);
  const [selectedDateRange, setSelectedDateRange] = useState<DatesRangeValue>(() => {
    const today = moment().toDate();
    return [today, today];
  });
  const [appliedDateRange, setAppliedDateRange] = useState<DatesRangeValue>(selectedDateRange);
  const [hideCompleted, setHideCompleted] = useState<boolean>(false);
  const searchParams = new URLSearchParams(location.search);
  const searchParamStoreId = searchParams.get('storeId') || 'all';
  const { downloadExcelV2, loadingExport } = useDownloadExcel();

  const queryParams = useMemo(
    () => ({
      storeId: searchParamStoreId || 'all',
      dateFrom: appliedDateRange[0]
        ? moment(appliedDateRange[0]).format('YYYY-MM-DD')
        : moment().format('YYYY-MM-DD'),
      dateTo: appliedDateRange[1]
        ? moment(appliedDateRange[1]).format('YYYY-MM-DD')
        : moment().format('YYYY-MM-DD'),
      includeDelivered: hideCompleted ? 'false' : 'true'
    }),
    [searchParamStoreId, appliedDateRange, hideCompleted]
  );

  const {
    data,
    isLoading: isGetLoadingOrders,
    isError
  } = useGetScheduledDeliveries(companyId, queryParams);

  // Flatten all orders from all stores into a single array
  const tableInfoData: ScheduledDeliveriesOrderData[] = useMemo(() => {
    if (!data?.items) return [];
    return data.items.flatMap((store: any) =>
      (store.orders || []).map((order: any) => ({
        ...order,
        storeName: store.storeName
      }))
    );
  }, [data]);

  const totalRowsCount = useMemo(() => tableInfoData.length, [tableInfoData]);
  const summary = useMemo(() => {
    const grandTotals = data?.summary?.grandTotals || {};
    const units = data?.summary?.units || {};
    const grandTotalsByUnit = data?.summary?.grandTotalsByUnit || {};

    const weightUnit = units.weightUnit || 'kg';
    const lengthUnit = units.lengthUnit || 'm';

    const normalPieces = Number(grandTotals.normalPieces || 0);
    const weight = Number(grandTotals.weight || 0);
    const length = Number(grandTotals.length || 0);
    const weightKg = Number(grandTotalsByUnit?.weight?.kg || 0);
    const weightLb = Number(grandTotalsByUnit?.weight?.lb || 0);
    const lengthM = Number(grandTotalsByUnit?.length?.m || 0);
    const lengthFt = Number(grandTotalsByUnit?.length?.ft || 0);

    const formatQty = (value: number) =>
      new Intl.NumberFormat('es-MX', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2
      }).format(value);

    const weightText =
      weightUnit === 'mixed'
        ? `${formatQty(weightKg)} kg | ${formatQty(weightLb)} lb`
        : `${formatQty(weight)} ${weightUnit}`;

    const lengthText =
      lengthUnit === 'mixed'
        ? `${formatQty(lengthM)} m | ${formatQty(lengthFt)} ft`
        : `${formatQty(length)} ${lengthUnit}`;

    return {
      normalPiecesText: formatQty(normalPieces),
      weightText,
      lengthText
    };
  }, [data]);

  return (
    <CatalogContainer>
      <CatalogListHeader
        title={t('ReportsPage:scheduledDeliveriesReport.title')}
        showCreateButton={false}
      />
      <Grid>
        <Grid.Col span={{ xs: 12, md: 'auto' }}>
          <DatePickerInput
            clearable={false}
            type="range"
            label={t('FieldName:dateRange')}
            placeholder={t('FieldName:dateRange')}
            value={selectedDateRange}
            onChange={(value) => {
              setSelectedDateRange(value);
              if (!value[0] || !value[1]) return;
              setAppliedDateRange(value);
            }}
            allowSingleDateInRange
          />
        </Grid.Col>
        <Grid.Col span={{ xs: 12, md: 'auto' }} style={{ alignSelf: 'flex-end' }}>
          <Checkbox
            label={
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                <span>{t('ReportsPage:scheduledDeliveriesReport.hideCompleted')}</span>
                <Tooltip
                  label={t('ReportsPage:scheduledDeliveriesReport.hideCompletedTooltip')}
                  multiline
                  maw={280}
                  withArrow
                >
                  <ThemeIcon
                    component="span"
                    size={16}
                    radius="xl"
                    variant="transparent"
                    color="gray"
                    style={{ cursor: 'help', flexShrink: 0 }}
                  >
                    <IconInfoCircle size={14} stroke={1.8} />
                  </ThemeIcon>
                </Tooltip>
              </span>
            }
            checked={hideCompleted}
            onChange={(e) => setHideCompleted(e.currentTarget.checked)}
          />
        </Grid.Col>
        <Grid.Col span={{ xs: 12, md: 'content' }} style={{ alignSelf: 'flex-end' }}>
          <Tooltip label={t('CommonButtons:exportList')}>
            <ActionIcon
              disabled={loadingExport}
              onClick={() => {
                downloadExcelV2(async () => {
                  try {
                    return await exportScheduledDeliveriesReportCSV(companyId, queryParams);
                  } catch (e) {
                    throw e;
                  }
                }, `Reporte_Entregas_Programadas_${searchParamStoreId || 'all'}`);
              }}
              variant="filled"
              color="#1D6F42"
              aria-label="Descargar_Listado_CSV"
            >
              <IconFileSpreadsheet style={{ width: '70%', height: '70%' }} stroke={1.5} />
            </ActionIcon>
          </Tooltip>
        </Grid.Col>
        <Grid.Col span={12}>
          <Paper withBorder p="sm" radius="md">
            <Grid gutter="xs">
              <Grid.Col span={{ xs: 12, md: 3 }}>
                <Text fw={700}>
                  {t('ReportsPage:scheduledDeliveriesReport.summary.totalHomeServices', {
                    count: totalRowsCount
                  })}
                </Text>
              </Grid.Col>
              <Grid.Col span={{ xs: 12, md: 3 }}>
                <Text size="sm" c="dimmed">
                  {t('ReportsPage:scheduledDeliveriesReport.summary.pieces')}
                </Text>
                <Text fw={700}>{summary.normalPiecesText}</Text>
              </Grid.Col>
              <Grid.Col span={{ xs: 12, md: 3 }}>
                <Text size="sm" c="dimmed">
                  {t('ReportsPage:scheduledDeliveriesReport.summary.weight')}
                </Text>
                <Text fw={700}>{summary.weightText}</Text>
              </Grid.Col>
              <Grid.Col span={{ xs: 12, md: 3 }}>
                <Text size="sm" c="dimmed">
                  {t('ReportsPage:scheduledDeliveriesReport.summary.length')}
                </Text>
                <Text fw={700}>{summary.lengthText}</Text>
              </Grid.Col>
            </Grid>
          </Paper>
        </Grid.Col>
        <Grid.Col span={12}>
          <TableView
            tableInfoData={tableInfoData}
            totalRowsCount={totalRowsCount}
            isError={isError}
            isLoading={isGetLoadingOrders}
          />
        </Grid.Col>
      </Grid>
    </CatalogContainer>
  );
};

export default ScheduledDeliveriesReport;
